import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Button, Input, Select, TextArea, PaywallModal } from "../../../components/ui";
import { useFormValidation } from "../../../hooks";
import { apiFetch, PaywallError } from "../../../services/apiClient";

interface CampaignFormData {
  name: string;
  type: string;
  setting: string;
  description: string;
  world: string;
  isPublic: boolean;
  maxPlayers: number;
}

const CreateCampaignPage: React.FC = () => {
  const navigate = useNavigate();
  const [paywall, setPaywall] = useState<{ resource: string; limit: number } | null>(null);

  const form = useFormValidation<CampaignFormData>({
    initialValues: {
      name: "",
      type: "",
      setting: "",
      description: "",
      world: "",
      isPublic: false,
      maxPlayers: 4,
    },
    validationRules: {
      name: {
        required: true,
        minLength: 3,
        maxLength: 50,
      },
      type: {
        required: true,
        maxLength: 50,
      },
      setting: {
        required: true,
        maxLength: 100,
      },
      description: {
        maxLength: 1000,
      },
      world: {
        maxLength: 100,
      },
      maxPlayers: {
        required: true,
        custom: (value: number) => {
          if (value < 1 || value > 20) {
            return "Max players must be between 1 and 20";
          }
          return null;
        },
      },
    },
    onSubmit: async (values) => {
      try {
        const payload = {
          name: values.name,
          type: values.type,
          setting: values.setting,
          description: values.description || undefined,
          world: values.world || undefined,
          isPublic: values.isPublic,
          maxPlayers: values.maxPlayers,
        };

        const response = await apiFetch("/api/protected/campaigns/campaign", {
          method: "POST",
          body: JSON.stringify(payload),
        });

        if (response.ok) {
          const data = await response.json();
          navigate(`/protected/campaigns/${data.campaign.id}`);
        } else {
          const errorText = await response.text();
          throw new Error(errorText || "Failed to create campaign");
        }
      } catch (error) {
        if (error instanceof PaywallError) {
          setPaywall({ resource: error.resource, limit: error.limit });
          return;
        }
        console.error("Error creating campaign:", error);
        throw error;
      }
    },
  });

  const handleCancel = () => {
    navigate("/protected/campaigns");
  };

  // Custom handler for checkbox
  const handleCheckboxChange =
    (field: keyof CampaignFormData) => (e: React.ChangeEvent<HTMLInputElement>) => {
      form.setValue(field, e.target.checked);
    };

  // Custom handler for number inputs
  const handleNumberChange =
    (field: keyof CampaignFormData) => (e: React.ChangeEvent<HTMLInputElement>) => {
      const value = parseInt(e.target.value) || 0;
      form.setValue(field, value);
    };

  return (
    <div className="p-6 bg-zinc-950 text-white min-h-screen">
      <div className="max-w-2xl mx-auto">
        <div className="bg-zinc-900 border border-zinc-800 p-6">
          <div className="flex justify-between items-center mb-6">
            <h1 className="text-2xl font-light text-white tracking-wide">Create New Chronicle</h1>
            <Button variant="ghost" onClick={handleCancel}>
              Back to Chronicles
            </Button>
          </div>

          <form onSubmit={form.handleSubmit}>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {/* Campaign Name */}
              <div>
                <Input
                  type="text"
                  id="name"
                  name="name"
                  label="Chronicle Name"
                  value={form.values.name}
                  onChange={form.handleChange("name")}
                  onBlur={form.handleBlur("name")}
                  error={form.errors.name}
                  placeholder="Enter chronicle name"
                  fullWidth
                  variant="filled"
                />
              </div>

              {/* Campaign Type */}
              <div>
                <Select
                  id="type"
                  name="type"
                  label="Campaign Type"
                  value={form.values.type}
                  onChange={form.handleChange("type")}
                  onBlur={form.handleBlur("type")}
                  error={form.errors.type}
                  options={[
                    { value: "", label: "Select Type" },
                    { value: "D&D 5e", label: "D&D 5e" },
                    { value: "Pathfinder 2e", label: "Pathfinder 2e" },
                    { value: "Call of Cthulhu", label: "Call of Cthulhu" },
                    { value: "Cyberpunk 2020", label: "Cyberpunk 2020" },
                    { value: "World of Darkness", label: "World of Darkness" },
                    { value: "Custom", label: "Custom" },
                  ]}
                  fullWidth
                  variant="filled"
                />
              </div>

              {/* Campaign Setting */}
              <div>
                <Input
                  type="text"
                  id="setting"
                  name="setting"
                  label="Campaign Setting"
                  value={form.values.setting}
                  onChange={form.handleChange("setting")}
                  onBlur={form.handleBlur("setting")}
                  error={form.errors.setting}
                  placeholder="e.g., Fantasy, Sci-Fi, Modern"
                  fullWidth
                  variant="filled"
                />
              </div>

              {/* World */}
              <div>
                <Input
                  type="text"
                  id="world"
                  name="world"
                  label="World (Optional)"
                  value={form.values.world}
                  onChange={form.handleChange("world")}
                  onBlur={form.handleBlur("world")}
                  error={form.errors.world}
                  placeholder="e.g., Forgotten Realms, Eberron"
                  fullWidth
                  variant="filled"
                />
              </div>

              {/* Max Players */}
              <div>
                <Input
                  type="number"
                  id="maxPlayers"
                  name="maxPlayers"
                  label="Max Players"
                  value={form.values.maxPlayers}
                  onChange={handleNumberChange("maxPlayers")}
                  onBlur={form.handleBlur("maxPlayers")}
                  error={form.errors.maxPlayers}
                  min={1}
                  max={20}
                  fullWidth
                  variant="filled"
                />
              </div>

              {/* Is Public */}
              <div className="flex items-center">
                <Input
                  type="checkbox"
                  id="isPublic"
                  name="isPublic"
                  checked={form.values.isPublic}
                  onChange={handleCheckboxChange("isPublic")}
                  className="mr-3"
                />
                <label htmlFor="isPublic" className="text-amber-400">
                  Make Campaign Public
                </label>
              </div>
            </div>

            {/* Description */}
            <div className="mt-6">
              <TextArea
                id="description"
                name="description"
                label="Description (Optional)"
                value={form.values.description}
                onChange={form.handleChange("description")}
                onBlur={form.handleBlur("description")}
                error={form.errors.description}
                rows={4}
                placeholder="Describe your campaign..."
                showCharacterCount
                maxCharacters={1000}
                fullWidth
                variant="filled"
              />
            </div>

            {/* Action Buttons */}
            <div className="flex justify-end space-x-4 mt-8">
              <Button
                type="button"
                variant="secondary"
                onClick={handleCancel}
                disabled={form.isSubmitting}
              >
                Cancel
              </Button>
              <Button
                type="submit"
                variant="primary"
                disabled={form.isSubmitting}
                loading={form.isSubmitting}
              >
                {form.isSubmitting ? "Creating..." : "Create Campaign"}
              </Button>
            </div>
          </form>
        </div>
      </div>
      <PaywallModal
        open={!!paywall}
        resource={paywall?.resource ?? ""}
        limit={paywall?.limit ?? 0}
        onClose={() => setPaywall(null)}
      />
    </div>
  );
};

export default CreateCampaignPage;
